package com.evoapps.evoshift.domain.model

import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toInstant
import kotlinx.datetime.toLocalDateTime
import kotlinx.serialization.Serializable
import kotlin.time.Instant

/** SH-258: how the workplace's printed reports look (`workplaces/{id}.settings.reports`). */
@Serializable
data class ReportSettings(
    val timesheet: TimesheetReportSettings = TimesheetReportSettings()
)

/** SH-258: which hours the working-time record lists and counts. */
@Serializable
enum class TimesheetHoursSource {
    /** The roster's planned times only: the "Plan" column, no clock-in column. */
    PLAN,

    /** The registered (clocked) times only: shifts without a registration are left out. */
    ACTUAL,

    /**
     * The registered times where there are any, otherwise the plan, marked "–*" and explained by
     * the footnote (the layout before SH-258).
     */
    COMBINED;

    companion object {
        fun fromName(name: String?): TimesheetHoursSource = entries.firstOrNull { it.name == name } ?: COMBINED
    }
}

/**
 * SH-258: which way the start and the end of a shift round. Each side rounds on its own:
 * [START_UP_END_DOWN] never counts more than the times say (in the employer's favour),
 * [START_DOWN_END_UP] never less (in the employee's favour).
 */
@Serializable
enum class TimeRoundingMode {
    /** Both to the nearest step; exactly half-way rounds up (07:52 → 07:45, 07:53 → 08:00 at 15 minutes). */
    NEAREST,

    /** The start up and the end down: 07:58–16:12 → 08:00–16:00 at 15 minutes. */
    START_UP_END_DOWN,

    /** The start down and the end up: 07:58–16:12 → 07:45–16:15 at 15 minutes. */
    START_DOWN_END_UP;

    companion object {
        fun fromName(name: String?): TimeRoundingMode = entries.firstOrNull { it.name == name } ?: NEAREST
    }
}

/** SH-258: the day of the week next to the date. */
@Serializable
enum class WeekdayFormat {
    NONE,

    /** "Wt" */
    SHORT,

    /** "Wtorek" */
    FULL;

    companion object {
        fun fromName(name: String?): WeekdayFormat = entries.firstOrNull { it.name == name } ?: SHORT
    }
}

/**
 * SH-258: the working-time record ("Ewidencja czasu pracy") as the workplace prints it. The
 * defaults are the layout of SH-251. Rounding changes only the printed record (its times and
 * totals), never the stored times or the labour cost.
 */
@Serializable
data class TimesheetReportSettings(
    val hoursSource: TimesheetHoursSource = TimesheetHoursSource.COMBINED,
    /** The step the times round to, one of [ROUNDING_CHOICES]; 0: not rounded. */
    val roundingMinutes: Int = 0,
    val roundingMode: TimeRoundingMode = TimeRoundingMode.NEAREST,
    /** The "Uwagi" column (Sunday, holiday, absence). */
    val showRemarks: Boolean = true,
    /** An empty cell on every day for the manager's signature. */
    val showManagerSignature: Boolean = false,
    val weekday: WeekdayFormat = WeekdayFormat.SHORT
) {
    val rounds: Boolean get() = roundingMinutes > 0

    /** [start]–[end] as the record shows and counts it (rounded when the workplace rounds). */
    fun round(start: LocalDateTime, end: LocalDateTime): Pair<LocalDateTime, LocalDateTime> =
        TimeRounding.span(start, end, roundingMinutes, roundingMode)

    /** The same with stored times ("2026-02-02T07:58:00"); unreadable times are returned as they are. */
    fun round(start: String, end: String): Pair<String, String> {
        if (!rounds) return start to end
        val s = runCatching { LocalDateTime.parse(start) }.getOrNull() ?: return start to end
        val e = runCatching { LocalDateTime.parse(end) }.getOrNull() ?: return start to end
        val (a, b) = round(s, e)
        return a.toString() to b.toString()
    }

    companion object {
        val ROUNDING_CHOICES = listOf(0, 5, 10, 15, 30)

        /** Tolerant of stored values from elsewhere: an unknown step means no rounding. */
        fun of(
            hoursSource: TimesheetHoursSource,
            roundingMinutes: Int,
            roundingMode: TimeRoundingMode,
            showRemarks: Boolean,
            showManagerSignature: Boolean,
            weekday: WeekdayFormat
        ) = TimesheetReportSettings(
            hoursSource,
            roundingMinutes.takeIf { it in ROUNDING_CHOICES } ?: 0,
            roundingMode, showRemarks, showManagerSignature, weekday
        )
    }
}

/**
 * SH-258: rounding clock times to a step of minutes. Seconds are dropped first (the record shows
 * minutes). A step divides the day (5, 10, 15, 30), so the steps fall on the same clock times
 * every day and a shift over midnight rounds across the date (23:58 → 00:00 the next day).
 */
object TimeRounding {
    enum class Direction { NEAREST, UP, DOWN }

    fun round(time: LocalDateTime, stepMinutes: Int, direction: Direction): LocalDateTime {
        val minutes = time.toInstant(TimeZone.UTC).epochSeconds.floorDiv(60L)
        if (stepMinutes <= 0) return fromMinutes(minutes)
        val step = stepMinutes.toLong()
        val rounded = when (direction) {
            Direction.DOWN -> minutes.floorDiv(step) * step
            Direction.UP -> -((-minutes).floorDiv(step)) * step
            // Half-way rounds up: 07:52 → 07:45 and 07:53 → 08:00 at 15; 07:55 → 08:00 at 10.
            Direction.NEAREST -> (minutes + step / 2).floorDiv(step) * step
        }
        return fromMinutes(rounded)
    }

    /**
     * [start]–[end] rounded by [mode]. A short shift whose rounded end would come before its
     * rounded start ends at the start (no time, never negative).
     */
    fun span(start: LocalDateTime, end: LocalDateTime, stepMinutes: Int, mode: TimeRoundingMode): Pair<LocalDateTime, LocalDateTime> {
        if (stepMinutes <= 0) return start to end
        val (startDirection, endDirection) = when (mode) {
            TimeRoundingMode.NEAREST -> Direction.NEAREST to Direction.NEAREST
            TimeRoundingMode.START_UP_END_DOWN -> Direction.UP to Direction.DOWN
            TimeRoundingMode.START_DOWN_END_UP -> Direction.DOWN to Direction.UP
        }
        val s = round(start, stepMinutes, startDirection)
        val e = round(end, stepMinutes, endDirection)
        return s to (if (e < s) s else e)
    }

    private fun fromMinutes(minutes: Long): LocalDateTime =
        Instant.fromEpochSeconds(minutes * 60).toLocalDateTime(TimeZone.UTC)
}
